/**
 * TrialStatusBanner — BOLT-031: Trial Lifecycle Management.
 *
 * Renders a persistent banner across all portal pages when the org is in a
 * trial-related status. Shows days remaining and an upgrade CTA.
 *
 * Statuses:
 *   - trialing: subtle info banner — "X days left in your trial"
 *   - trial_warning: yellow warning — "Your trial expires in X days" + Upgrade Now
 *   - trial_expired: red error — "Your trial has expired" + Upgrade Now
 *
 * The "Upgrade Now" button (org_owner only) opens the Maxio billing portal
 * where the customer can add a payment method. Maxio automatically converts
 * the trial to paid when it ends if a payment method is on file.
 */
import { Alert, Button, CircularProgress } from '@mui/material';
import { BRAND } from '@crm98/shared';
import { useBillingStatus, useCreatePortalSession } from '@/lib/api/billing.js';
import { useAuthStore } from '@/store/auth.js';
import { useToast } from '@/hooks/useToast.js';

function daysUntil(isoDate: string): number {
  const diff = new Date(isoDate).getTime() - Date.now();
  return Math.ceil(diff / (24 * 60 * 60 * 1000));
}

export function TrialStatusBanner() {
  const role = useAuthStore((s) => s.role);
  const { data } = useBillingStatus();
  const portalMutation = useCreatePortalSession();
  const { toast } = useToast();

  // Don't render if data hasn't loaded or org isn't in a trial state.
  if (!data) return null;

  const status = data.org_status;
  const isTrialState =
    status === 'trialing' || status === 'trial_warning' || status === 'trial_expired';
  if (!isTrialState) return null;

  // Super admins don't need to see trial banners (they're not the org owner).
  if (role === 'super_admin') return null;

  const isOwner = role === 'org_owner';

  const periodEnd = data.subscription?.current_period_end;
  const rawDays = periodEnd ? daysUntil(periodEnd) : 0;
  const days = Math.max(rawDays, 0);
  const daysLabel = days === 0 ? 'less than a day' : `${days} ${days === 1 ? 'day' : 'days'}`;

  const handleUpgrade = () => {
    portalMutation.mutate(undefined, {
      onSuccess: (resp) => {
        window.open(resp.portal_url, '_blank', 'noopener,noreferrer');
      },
      onError: () => {
        toast('Unable to open billing portal. Please try again.', 'error');
      },
    });
  };

  const upgradeButton = isOwner ? (
    <Button
      color="inherit"
      size="small"
      onClick={handleUpgrade}
      disabled={portalMutation.isPending}
      startIcon={
        portalMutation.isPending ? <CircularProgress size={14} color="inherit" /> : undefined
      }
    >
      Upgrade Now
    </Button>
  ) : undefined;

  const billingButton = isOwner ? (
    <Button color="inherit" size="small" href="/org-settings?tab=billing">
      Manage Billing
    </Button>
  ) : undefined;

  if (status === 'trial_expired') {
    return (
      <Alert severity="error" sx={{ borderRadius: 0 }} action={upgradeButton}>
        Your trial has expired. Upgrade to continue using {BRAND.productName}.
      </Alert>
    );
  }

  if (status === 'trial_warning') {
    return (
      <Alert severity="warning" sx={{ borderRadius: 0 }} action={upgradeButton}>
        Your trial expires in {daysLabel}. Add a payment method to keep your account active.
      </Alert>
    );
  }

  // trialing — subtle info banner
  return (
    <Alert severity="info" sx={{ borderRadius: 0 }} action={billingButton}>
      {daysLabel} left in your free trial.
    </Alert>
  );
}
